import styled from "styled-components";
import { Form, Input, Button } from "antd";
import { UseQueryResult, useMutation } from "@tanstack/react-query";
import {
  openNotification,
  IEnterpriseConnectorRequest,
  createEnterpriseConnector,
  updateEnterpriseConnector,
  EnterpriseConnectorType,
  IErrorResponse,
} from "@dataone/lib";
import SubHeader from "./SubHeader";
import { ErrorStep } from "../../steps/ErrorStep";
import { IDataSourceApp } from "../datasets-sources/DatasetsSources.config";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { AxiosError } from "axios";
import { useState } from "react";

interface UpdateEnterpriseConnectorFormProps {
  failed: boolean;
  enterpriseConnectorData: IEnterpriseConnectorRequest | null;
  dataSource: IDataSourceApp;
  formMode: "update" | "create";
  setILoading: (submitClicked: boolean) => void;
  setCurrent: (current: number) => void;
  setFailed: (failed: boolean) => void;
  onCreateSuccess: () => void;
  refetchDatasets?: (options?: {
    throwOnError: boolean;
    cancelRefetch: boolean;
  }) => Promise<UseQueryResult>;
}

const UpdateEnterpriseConnectorForm = ({
  formMode,
  failed,
  enterpriseConnectorData,
  dataSource,
  setILoading,
  setCurrent,
  setFailed,
  onCreateSuccess,
  refetchDatasets,
}: UpdateEnterpriseConnectorFormProps) => {
  const [enterpriseConnectorForm] = Form.useForm();
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { mutate: addEnterpriseConnector, isLoading: isCreating } = useMutation(
    createEnterpriseConnector
  );
  const { mutate: editEnterpriseConnector, isLoading: isUpdating } =
    useMutation(updateEnterpriseConnector);

  const handleOnSuccess = (message: string) => {
    // openNotification(message);
    setCurrent(2);
  };

  const handleSubmitForm = async (values: IEnterpriseConnectorRequest) => {
    setILoading(true);
    formMode === "update"
      ? enterpriseConnectorData &&
        editEnterpriseConnector(
          {
            ...values,
            type: dataSource.type as EnterpriseConnectorType,
            id: enterpriseConnectorData.id,
            integration: values.integration.toString(),
          },
          {
            onSuccess: ({ message }) => {
              handleOnSuccess(message);
              setILoading(false);
              refetchDatasets?.();
            },
            onError: (error) => {
              setILoading(false);
              setFailed(true);
              const { response } = error as AxiosError<IErrorResponse>;
              setErrorMessage(response?.data.message!);
            },
          }
        )
      : addEnterpriseConnector(
          {
            ...values,
            type: dataSource.type as EnterpriseConnectorType,
            integration: values.integration.toString(),
          },
          {
            onSuccess: ({ message }) => {
              handleOnSuccess(message);
              setILoading(false);
              onCreateSuccess();
            },
            onError: (error) => {
              setILoading(false);
              setFailed(true);
              const { response } = error as AxiosError<IErrorResponse>;
              setErrorMessage(response?.data.message!);
            },
          }
        );
  };

  const rules = [
    {
      whitespace: true,
      required: true,
      message: "Please provide a value for this field.",
    },
  ];

  return (
    <>
      {failed ? (
        <ErrorStep errorMessage={errorMessage} />
      ) : (
        <>
          <StyledEnterpriseConnectorForm $maxHeight="400px">
            <SubHeader icon={dataSource?.icon} label={dataSource.title} />
            <Form
              form={enterpriseConnectorForm}
              className="enterprise-connector-form"
              layout="vertical"
              onFinish={handleSubmitForm}
              {...(enterpriseConnectorData && {
                initialValues: {
                  ...enterpriseConnectorData,
                  integration: enterpriseConnectorData.integration.split(","),
                },
              })}
            >
              <Form.Item
                rules={rules}
                name="displayName"
                label={<StyledLabel>Display name</StyledLabel>}
              >
                <Input
                  placeholder="Display name"
                  maxLength={255}
                  className="input"
                />
              </Form.Item>
              <Form.Item
                rules={formMode === "create" ? rules : []}
                name="token"
                label={<StyledLabel>API Token</StyledLabel>}
              >
                <Input maxLength={255} className="input" placeholder="Token" />
              </Form.Item>

              {dataSource.type === EnterpriseConnectorType.TRELLO && (
                <Form.Item
                  rules={rules}
                  name="apiKey"
                  label={<StyledLabel>API Key</StyledLabel>}
                >
                  <Input
                    maxLength={255}
                    className="input"
                    placeholder="API Key"
                  />
                </Form.Item>
              )}
              {dataSource.type === EnterpriseConnectorType.JIRA && (
                <Form.Item
                  rules={rules}
                  name="email"
                  label={<StyledLabel>Email</StyledLabel>}
                >
                  <Input
                    maxLength={255}
                    className="input"
                    placeholder="Email"
                  />
                </Form.Item>
              )}
              {dataSource.type === EnterpriseConnectorType.JIRA && (
                <Form.Item
                  rules={rules}
                  name="workspaceUrl"
                  label={<StyledLabel>Workspace Link</StyledLabel>}
                >
                  <Input maxLength={255} className="input" placeholder="URL" />
                </Form.Item>
              )}
              <Form.List initialValue={[""]} name="integration">
                {(fields, { add, remove }, { errors }) => (
                  <>
                    {fields.map((field, index) => (
                      <Form.Item
                        label={
                          index === 0 ? (
                            <StyledLabel $isRequired>Integration</StyledLabel>
                          ) : (
                            ""
                          )
                        }
                        required={false}
                        key={field.key}
                      >
                        <Form.Item
                          {...field}
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: "Please input the integration id.",
                            },
                          ]}
                          noStyle
                        >
                          <StyledIntegrationInput
                            $isDeleteVisible={fields.length > 1}
                            placeholder="Intergration id"
                          />
                        </Form.Item>
                        {fields.length > 1 ? (
                          <MinusCircleOutlined
                            className="dynamic-delete-button"
                            onClick={() => remove(field.name)}
                          />
                        ) : null}
                      </Form.Item>
                    ))}
                    <Form.Item>
                      {dataSource.type === EnterpriseConnectorType.SLACK && (
                        <Button
                          onClick={() => add()}
                          style={{
                            width:
                              fields.length > 1 ? "calc(100% - 35px)" : "100%",
                          }}
                          icon={<PlusOutlined />}
                        >
                          Add integration id
                        </Button>
                      )}
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Form>
          </StyledEnterpriseConnectorForm>
          <StyledFooterButtons isCreating={isCreating} isUpdating={isUpdating}>
            <Button
              className="submit-button"
              key="submit"
              type="primary"
              onClick={() => enterpriseConnectorForm.submit()}
            >
              {formMode === "update"
                ? isUpdating
                  ? "Updating ..."
                  : "Update"
                : isCreating
                ? "Creating ..."
                : "Create"}
            </Button>
          </StyledFooterButtons>
        </>
      )}
    </>
  );
};

const StyledEnterpriseConnectorForm = styled.div<{ $maxHeight: string }>`
  .enterprise-connector-form {
    padding-left: 10%;
    padding-right: 10%;
  }
  .input {
    height: 40px;
    border-radius: 6px;
    background: #eff1fd !important;
    border: none;
    // background: #fff;
    box-shadow: 0px 4px 4px 0px #fff;
    font-size: 12px;
  }
  .ant-form-item-explain-error {
    font-size: 12px;
  }
  max-height: ${(p) => p.$maxHeight};
  overflow-y: auto;
  margin-block: 35px;
  // width: 40vw;
  min-width: 450px;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: #fff;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #8b8b8b;
    border-radius: 7px;
  }

  &::-webkit-scrollbar-button {
    display: block;
    height: 16px;
    width: 16px;
    background-color: #fff;
    border: none;
  }

  &::-webkit-scrollbar-button:single-button:vertical:decrement {
    height: 12px;
    width: 16px;
    background-position: center 6px;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='rgb(96, 96, 96)'><polygon points='50,00 0,50 100,50'/></svg>");
  }
  &::-webkit-scrollbar-button:single-button:vertical:increment {
    height: 12px;
    width: 16px;
    background-position: center center;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='rgb(96, 96, 96)'><polygon points='0,0 100,0 50,50'/></svg>");
  }

  .anticon-minus-circle {
    font-size: 18px;
    margin-left: 5px;
  }

  .ant-btn-default {
    color: #000 !important;
    background-color: #eff1fd;
    border-color: #eff1fd !important;
    border-radius: 6px;
    height: 40px;
    font-size: 12px;
  }

  .ant-form-item-with-help .ant-input {
    border-color: #ff4d4f !important;
  }
`;
const StyledFooterButtons = styled.div<{
  isCreating: boolean;
  isUpdating: boolean;
}>`
  .submit-button {
    position: absolute;
    right: 40px;
    bottom: 15px;
    font-size: 13px;
    width: 100px;
    cursor: ${({ isCreating, isUpdating }) =>
      isCreating || isUpdating ? "not-allowed" : "pointer"};
  }
`;
const StyledLabel = styled.span<{ $isRequired?: boolean }>`
  color: rgba(4, 7, 38, 0.8);
  font-size: 13px;
  font-weight: 600;

  ${({ $isRequired }) =>
    $isRequired &&
    `
    &:before {
      display: inline-block;
      margin-inline-end: 4px;
      color: #ff4d4f;
      font-size: 14px;
      font-family: SimSun,sans-serif;
      line-height: 1;
      content: "*";
    }
  `}
`;
const StyledIntegrationInput = styled(Input)<{ $isDeleteVisible: boolean }>`
  margin-right: 10px;
  width: ${({ $isDeleteVisible }) =>
    $isDeleteVisible ? "calc(100% - 35px)" : "100%"};
  height: 40px;
  border-radius: 6px;
  background: #eff1fd !important;
  border: none;
  // background: #fff;
  box-shadow: 0px 4px 4px 0px #fff !important;
  transition-duration: 0s;
  font-size: 12px;
`;

export default UpdateEnterpriseConnectorForm;
